import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiAxios from 'helpers/apiAxios';

const BASE = '/my/sent_emails';

const cleanParams = (params) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''));

// Initial load — replaces the row list
export const fetchSentEmails = createAsyncThunk(
  'MyInbox/fetch',
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(BASE, { params: cleanParams(params) });
      return res.data;
    } catch (e) { return rejectWithValue(e.response?.data); }
  }
);

// Infinite-scroll load — appends to the row list
export const fetchMoreSentEmails = createAsyncThunk(
  'MyInbox/fetchMore',
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(BASE, { params: cleanParams(params) });
      return res.data;
    } catch (e) { return rejectWithValue(e.response?.data); }
  }
);

export const fetchSentEmailDetail = createAsyncThunk(
  'MyInbox/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/${id}`);
      return { id, data: res.data.data };
    } catch (e) { return rejectWithValue(e.response?.data); }
  }
);

export const markEmailRead = createAsyncThunk(
  'MyInbox/markRead',
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.patch(`${BASE}/${id}/mark_read`);
      return { id, read_at: res.data.read_at };
    } catch (e) { return rejectWithValue(e.response?.data); }
  }
);

export const markAllEmailsRead = createAsyncThunk(
  'MyInbox/markAllRead',
  async (_, { rejectWithValue }) => {
    try {
      await apiAxios.patch(`${BASE}/mark_all_read`);
      return true;
    } catch (e) { return rejectWithValue(e.response?.data); }
  }
);

const inboxSlice = createSlice({
  name: 'my_inbox',
  initialState: {
    rows:         [],
    totalRows:    0,
    folders:      {},
    unreadCount:  0,
    hasMore:      false,
    selected:     null,
    loading:      false,
    loadingMore:  false,
    loadingDetail: false,
  },
  reducers: {
    clearSelected: (s) => { s.selected = null; },
  },
  extraReducers: (builder) => {
    builder
      // ── Initial fetch (replaces list) ──
      .addCase(fetchSentEmails.pending,   (s) => { s.loading = true; })
      .addCase(fetchSentEmails.fulfilled, (s, a) => {
        const rows     = a.payload.rows || [];
        const total    = a.payload.total_rows || 0;
        s.loading      = false;
        s.rows         = rows;
        s.totalRows    = total;
        s.folders      = a.payload.folders || {};
        s.unreadCount  = a.payload.unread_count ?? s.unreadCount;
        s.hasMore      = rows.length < total;
      })
      .addCase(fetchSentEmails.rejected,  (s) => { s.loading = false; })

      // ── Infinite-scroll fetch (appends) ──
      .addCase(fetchMoreSentEmails.pending,   (s) => { s.loadingMore = true; })
      .addCase(fetchMoreSentEmails.fulfilled, (s, a) => {
        const newRows  = a.payload.rows || [];
        s.loadingMore  = false;
        s.rows         = [...s.rows, ...newRows];
        s.hasMore      = s.rows.length < s.totalRows;
      })
      .addCase(fetchMoreSentEmails.rejected,  (s) => { s.loadingMore = false; })

      // ── Fetch detail (auto-marks read) ──
      .addCase(fetchSentEmailDetail.pending,   (s) => { s.loadingDetail = true; })
      .addCase(fetchSentEmailDetail.fulfilled, (s, a) => {
        s.loadingDetail = false;
        s.selected = a.payload.data;
        const row = s.rows.find(r => r.id === a.payload.id);
        if (row && !row.read_at) {
          row.read_at = new Date().toISOString();
          s.unreadCount = Math.max(0, s.unreadCount - 1);
        }
      })
      .addCase(fetchSentEmailDetail.rejected,  (s) => { s.loadingDetail = false; })

      // ── Mark single read ──
      .addCase(markEmailRead.fulfilled, (s, a) => {
        const row = s.rows.find(r => r.id === a.payload.id);
        if (row && !row.read_at) {
          row.read_at = a.payload.read_at;
          s.unreadCount = Math.max(0, s.unreadCount - 1);
        }
        if (s.selected?.id === a.payload.id) s.selected = { ...s.selected, read_at: a.payload.read_at };
      })

      // ── Mark all read ──
      .addCase(markAllEmailsRead.fulfilled, (s) => {
        const now = new Date().toISOString();
        s.rows = s.rows.map(r => r.read_at ? r : { ...r, read_at: now });
        s.unreadCount = 0;
      });
  },
});

export const { clearSelected } = inboxSlice.actions;
export default inboxSlice.reducer;
